import { SxProps, Theme } from "@mui/material";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import { focusRing } from "theme/colors";
import { tableColumns } from "./SignRequests.styles";
import signRequestsTokens from "./SignRequests.tokens";
import signRequestsFiltersTokens, {
  signRequestsTagColors,
  SignRequestsTagColor,
  TABLET_WIDE_QUERY,
} from "./SignRequestsFilters.tokens";

const {
  toolbar,
  dropdown,
  sharedBanner,
  chips,
  filterBar,
  filterPanel,
  table,
  tags,
  picker,
  drawer,
  dialogs,
} = signRequestsFiltersTokens;

const focusVisible = {
  "&.Mui-focusVisible": {
    outline: `2px solid ${focusRing}`,
    outlineOffset: "2px",
  },
} as const;

/** Chrome shared by the search field and the three triggers beside it. */
const toolbarBox = {
  display: "flex",
  alignItems: "center",
  height: toolbar.height,
  boxSizing: "border-box",
  bgcolor: toolbar.bg,
  border: `1px solid ${toolbar.border}`,
  borderRadius: toolbar.radius,
  px: toolbar.paddingX,
} as const;

/** Columns of the request table, in source order. */
export type SignRequestsTableColumn =
  | "subject"
  | "signers"
  | "sender"
  | "status"
  | "tags"
  | "created"
  | "expires"
  | "actions";

/**
 * Which widths a column exists at. A hidden cell is `display: none`, which
 * also takes it out of grid auto-placement — so the header row and every
 * request row stay in step with whichever column template is in force.
 */
const columnVisibility = (
  column: SignRequestsTableColumn,
  shown: "block" | "flex",
) => {
  switch (column) {
    case "signers":
      return { display: { xs: "none", lg: shown } };
    case "sender":
    case "created":
      return { display: { xs: "none", xl: shown } };
    case "tags":
      return {
        display: { xs: "none", lg: shown },
        [TABLET_WIDE_QUERY]: { display: shown },
      };
    case "expires":
      return { display: { xs: "none", sm: shown } };
    default:
      return { display: shown };
  }
};

/** One line of plain cell text — sender, created and expiry dates. */
const textCell = {
  minWidth: 0,
  fontSize: table.dateFontSize,
  color: table.cellColor,
  lineHeight: 1.35,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const;

/** The Sort / Views / Filters buttons. */
const triggerBase = {
  ...toolbarBox,
  ...focusVisible,
  gap: toolbar.triggerGap,
  flex: { xs: "1 1 0", sm: "0 0 auto" },
  minWidth: 0,
  justifyContent: { xs: "center", sm: "flex-start" },
  fontFamily: "inherit",
  fontSize: toolbar.triggerFontSize,
  fontWeight: toolbar.triggerFontWeight,
  color: toolbar.triggerColor,
  whiteSpace: "nowrap",
  "&:hover": { borderColor: toolbar.hoverBorder },
  "&.Mui-disabled": { opacity: 0.6 },
} as const;

/** Small square icon button used across the menus, drawer and dialogs. */
const iconAction = (size: number, radius: string) =>
  ({
    width: size,
    height: size,
    borderRadius: radius,
    flexShrink: 0,
    ...focusVisible,
  }) as const;

/** Text that reads as a link without being one — "Manage tags", "Manage views". */
const underlineLink = {
  p: 0,
  minWidth: 0,
  height: "auto",
  textDecoration: "underline",
  textUnderlineOffset: "3px",
  "&:hover": { background: "none", textDecoration: "underline" },
} as const;

const signRequestsFiltersStyles = {
  /* ── Toolbar ───────────────────────────────────────────────────────────── */

  /**
   * Below `sm` the three triggers drop onto their own line under the search
   * field; the reference stops at the tablet, and one row of four controls
   * does not fit a phone.
   */
  toolbar: {
    display: "flex",
    alignItems: "center",
    flexWrap: { xs: "wrap", sm: "nowrap" },
    gap: toolbar.gap,
    mb: { xs: 0, md: toolbar.marginBottom },
  } as SxProps<Theme>,

  searchField: {
    ...toolbarBox,
    flex: { xs: "1 1 100%", sm: 1 },
    minWidth: 0,
    gap: toolbar.searchGap,
    "&:focus-within": { borderColor: toolbar.focusBorder },
  } as SxProps<Theme>,

  searchGlyph: {
    width: toolbar.searchIconSize,
    height: toolbar.searchIconSize,
    color: toolbar.searchIconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  searchInput: {
    flex: 1,
    minWidth: 0,
    "& .MuiInputBase-input": {
      p: 0,
      height: "auto",
      fontSize: toolbar.inputFontSize,
      fontWeight: 500,
      lineHeight: "normal",
      color: signRequestsTokens.toolbar.inputColor,
      "&::placeholder": {
        color: signRequestsTokens.toolbar.placeholderColor,
        opacity: 1,
      },
    },
  } as SxProps<Theme>,

  trigger: triggerBase as SxProps<Theme>,

  viewsTrigger: {
    ...triggerBase,
    maxWidth: { sm: toolbar.viewsMaxWidth },
  } as SxProps<Theme>,

  triggerGlyph: {
    width: toolbar.triggerIconSize,
    height: toolbar.triggerIconSize,
    color: toolbar.triggerIconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  triggerLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  triggerCaret: {
    width: toolbar.caretSize,
    height: toolbar.caretSize,
    color: toolbar.caretColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  /* ── Sort / Views dropdowns ────────────────────────────────────────────── */

  sortMenuPaper: { width: dropdown.sortWidth } as SxProps<Theme>,
  viewsMenuPaper: { width: dropdown.viewsWidth } as SxProps<Theme>,

  menuItem: (active: boolean) =>
    ({
      gap: dropdown.itemGap,
      fontSize: dropdown.itemFontSize,
      fontWeight: active ? dropdown.activeFontWeight : dropdown.restFontWeight,
      color: dropdown.itemColor,
    }) as SxProps<Theme>,

  /** Fixed-width slot so labels line up whether or not a row is checked. */
  menuCheckSlot: {
    width: dropdown.checkSize,
    height: dropdown.checkSize,
    display: "flex",
    color: dropdown.checkColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  menuCheck: {
    width: dropdown.checkSize,
    height: dropdown.checkSize,
  } as SxProps<Theme>,

  menuItemLabel: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  /** Spinner trailing a label while its list is still being read. */
  inlineSpinner: {
    ml: "8px",
    verticalAlign: "middle",
  } as SxProps<Theme>,

  menuSectionLabel: {
    p: dropdown.sectionLabelPadding,
    fontSize: dropdown.sectionLabelFontSize,
    fontWeight: dropdown.sectionLabelFontWeight,
    letterSpacing: dropdown.sectionLabelSpacing,
    textTransform: "uppercase",
    color: dropdown.sectionLabelColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /** A view row: the selectable item plus its own overflow trigger. */
  viewRow: {
    display: "flex",
    alignItems: "center",
  } as SxProps<Theme>,

  viewRowItem: (active: boolean) =>
    ({
      flex: 1,
      minWidth: 0,
      gap: dropdown.itemGap,
      fontSize: dropdown.itemFontSize,
      fontWeight: active ? dropdown.activeFontWeight : dropdown.restFontWeight,
      color: dropdown.itemColor,
    }) as SxProps<Theme>,

  viewKebab: {
    ...iconAction(dropdown.kebabSize, dropdown.kebabRadius),
    color: dropdown.kebabColor,
    "&:hover": {
      bgcolor: dropdown.kebabHoverBg,
      color: dropdown.kebabHoverColor,
    },
  } as SxProps<Theme>,

  viewKebabGlyph: { width: 16, height: 16 } as SxProps<Theme>,

  menuDivider: {
    height: "1px",
    bgcolor: dropdown.divider,
    my: "6px",
  } as SxProps<Theme>,

  saveViewItem: {
    gap: dropdown.itemGap,
    fontSize: dropdown.itemFontSize,
    fontWeight: dropdown.saveFontWeight,
    color: dropdown.saveColor,
    "&:hover": { bgcolor: dropdown.saveHoverBg, color: dropdown.saveColor },
  } as SxProps<Theme>,

  manageViewsItem: {
    p: dropdown.managePadding,
    fontSize: dropdown.manageFontSize,
    fontWeight: dropdown.manageFontWeight,
    color: dropdown.manageColor,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    "&:hover": { background: "none", color: dropdown.manageColor },
  } as SxProps<Theme>,

  dangerItem: { color: dropdown.dangerColor } as SxProps<Theme>,

  /** Inline delete confirm — replaces the view row it belongs to. */
  inlineConfirm: {
    display: "flex",
    flexDirection: "column",
    gap: dropdown.confirmGap,
    p: dropdown.confirmPadding,
    borderRadius: dropdown.confirmRadius,
    bgcolor: dropdown.confirmBg,
  } as SxProps<Theme>,

  inlineConfirmText: {
    fontSize: dropdown.confirmFontSize,
    fontWeight: 600,
    color: dropdown.confirmColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  inlineConfirmActions: {
    display: "flex",
    gap: dropdown.confirmGap,
    flexShrink: 0,
  } as SxProps<Theme>,

  /* ── Shared-link notice ────────────────────────────────────────────────── */

  sharedBanner: {
    display: "flex",
    alignItems: "center",
    flexWrap: { xs: "wrap", sm: "nowrap" },
    gap: sharedBanner.gap,
    p: sharedBanner.padding,
    mb: { xs: 0, md: sharedBanner.marginBottom },
    borderRadius: sharedBanner.radius,
    bgcolor: sharedBanner.bg,
    border: `1px solid ${sharedBanner.border}`,
  } as SxProps<Theme>,

  sharedBannerGlyph: {
    width: sharedBanner.iconSize,
    height: sharedBanner.iconSize,
    color: sharedBanner.iconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  sharedBannerText: {
    flex: 1,
    minWidth: 0,
    fontSize: sharedBanner.fontSize,
    color: sharedBanner.color,
    lineHeight: 1.4,
    "& b": { color: sharedBanner.strongColor },
  } as SxProps<Theme>,

  /** Outlined, but in the notice's own blue rather than the brand colour. */
  sharedBannerAction: {
    flexShrink: 0,
    whiteSpace: "nowrap",
    "&.MuiButton-root": {
      background: toolbar.bg,
      color: sharedBanner.actionColor,
      borderColor: sharedBanner.actionBorder,
      "&:hover": {
        background: toolbar.bg,
        borderColor: sharedBanner.actionColor,
      },
    },
  } as SxProps<Theme>,

  sharedBannerDismiss: {
    ...iconAction(sharedBanner.dismissSize, sharedBanner.dismissRadius),
    color: sharedBanner.actionColor,
    "&:hover": { bgcolor: sharedBanner.dismissHoverBg },
  } as SxProps<Theme>,

  sharedBannerDismissGlyph: {
    width: sharedBanner.dismissIconSize,
    height: sharedBanner.dismissIconSize,
  } as SxProps<Theme>,

  /* ── Filter bar ────────────────────────────────────────────────────────── */

  filterBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: filterBar.gap,
    mb: { xs: 0, md: filterBar.marginBottom },
  } as SxProps<Theme>,

  /** One applied filter: label | operator | value | remove. */
  filterPill: (open: boolean) =>
    ({
      display: "flex",
      alignItems: "stretch",
      maxWidth: "100%",
      height: filterBar.height,
      boxSizing: "border-box",
      borderRadius: filterBar.radius,
      border: `1px solid ${open ? filterBar.openBorder : filterBar.border}`,
      bgcolor: filterBar.bg,
      boxShadow: filterBar.shadow,
      overflow: "hidden",
      fontSize: filterBar.fontSize,
    }) as SxProps<Theme>,

  filterPillLabel: {
    display: "flex",
    alignItems: "center",
    gap: filterBar.labelGap,
    p: filterBar.labelPadding,
    bgcolor: filterBar.labelBg,
    borderRight: `1px solid ${filterBar.divider}`,
    fontWeight: filterBar.fontWeight,
    color: filterBar.labelColor,
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  filterPillGlyph: {
    width: filterBar.iconSize,
    height: filterBar.iconSize,
    color: filterBar.iconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  /** "is", "is any of", "contains" — dropped on a phone to save the width. */
  filterPillOperator: {
    display: { xs: "none", sm: "flex" },
    alignItems: "center",
    p: filterBar.opPadding,
    borderRight: `1px solid ${filterBar.divider}`,
    fontSize: filterBar.opFontSize,
    color: filterBar.opColor,
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  filterPillValue: (open: boolean) =>
    ({
      ...focusVisible,
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: filterBar.valueGap,
      minWidth: 0,
      maxWidth: filterBar.valueMaxWidth,
      p: filterBar.valuePadding,
      bgcolor: open ? filterBar.valueOpenBg : filterBar.bg,
      fontFamily: "inherit",
      fontSize: filterBar.fontSize,
      fontWeight: filterBar.fontWeight,
      color: filterBar.valueColor,
      whiteSpace: "nowrap",
      overflow: "hidden",
      "&:hover": {
        bgcolor: open ? filterBar.valueOpenBg : filterBar.valueHoverBg,
      },
    }) as SxProps<Theme>,

  /** One value inside a pill; a tag keeps its own chip colours. */
  filterPillPart: (color?: SignRequestsTagColor) =>
    ({
      display: "flex",
      alignItems: "center",
      gap: filterBar.partGap,
      minWidth: 0,
      height: filterBar.partHeight,
      p: color ? filterBar.partTagPadding : 0,
      borderRadius: filterBar.partRadius,
      bgcolor: color ? signRequestsTagColors[color].bg : "transparent",
      color: color ? signRequestsTagColors[color].fg : filterBar.valueColor,
      overflow: "hidden",
      textOverflow: "ellipsis",
    }) as SxProps<Theme>,

  filterPillDot: (dot: string) =>
    ({
      width: filterBar.partDotSize,
      height: filterBar.partDotSize,
      borderRadius: "50%",
      bgcolor: dot,
      flexShrink: 0,
    }) as SxProps<Theme>,

  filterPillMore: {
    fontSize: filterBar.moreFontSize,
    color: filterBar.moreColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  filterPillEmpty: {
    fontWeight: 600,
    color: filterBar.emptyColor,
  } as SxProps<Theme>,

  filterPillRemove: {
    ...focusVisible,
    width: filterBar.removeWidth,
    flexShrink: 0,
    borderLeft: `1px solid ${filterBar.divider}`,
    color: filterBar.removeColor,
    "&:hover": {
      bgcolor: filterBar.removeHoverBg,
      color: filterBar.removeHoverColor,
    },
  } as SxProps<Theme>,

  filterPillRemoveGlyph: {
    width: filterBar.removeIconSize,
    height: filterBar.removeIconSize,
  } as SxProps<Theme>,

  /** Dashed "Add filter" trigger. */
  filterAdd: {
    ...focusVisible,
    display: "flex",
    alignItems: "center",
    gap: filterBar.addGap,
    height: filterBar.height,
    boxSizing: "border-box",
    p: filterBar.addPadding,
    border: `1px dashed ${filterBar.addBorder}`,
    borderRadius: filterBar.radius,
    bgcolor: filterBar.bg,
    fontFamily: "inherit",
    fontSize: filterBar.fontSize,
    fontWeight: filterBar.fontWeight,
    color: filterBar.addColor,
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: filterBar.addHoverColor,
      color: filterBar.addHoverColor,
    },
    "&.Mui-disabled": { opacity: 0.6 },
  } as SxProps<Theme>,

  filterAddGlyph: {
    width: filterBar.addIconSize,
    height: filterBar.addIconSize,
  } as SxProps<Theme>,

  filterAddMenuPaper: { width: filterBar.addMenuWidth } as SxProps<Theme>,

  filterAddItem: {
    gap: dropdown.itemGap,
    fontSize: dropdown.itemFontSize,
    fontWeight: 600,
    color: dropdown.itemColor,
  } as SxProps<Theme>,

  /** Borderless actions on the bar: "All filters", "Save view". */
  filterBarLink: (color: string) =>
    ({
      ...focusVisible,
      display: "flex",
      alignItems: "center",
      gap: filterBar.linkGap,
      height: filterBar.height,
      p: filterBar.linkPadding,
      borderRadius: filterBar.radius,
      fontFamily: "inherit",
      fontSize: filterBar.fontSize,
      fontWeight: filterBar.fontWeight,
      color,
      whiteSpace: "nowrap",
      "&:hover": {
        bgcolor: filterBar.linkHoverBg,
        color: filterBar.linkHoverColor,
      },
      "&.Mui-disabled": { opacity: 0.6 },
    }) as SxProps<Theme>,

  filterBarLinkGlyph: {
    width: filterBar.linkIconSize,
    height: filterBar.linkIconSize,
  } as SxProps<Theme>,

  filterBarSpacer: { flex: 1 } as SxProps<Theme>,

  /* ── Filter panel (popover body and drawer pane) ───────────────────────── */

  panelPopover: {
    width: filterPanel.popoverWidth,
    maxWidth: "calc(100vw - 32px)",
    mt: "8px",
    display: "flex",
    flexDirection: "column",
    borderRadius: filterPanel.popoverRadius,
    border: `1px solid ${filterPanel.popoverBorder}`,
    boxShadow: filterPanel.popoverShadow,
  } as SxProps<Theme>,

  panelHead: {
    display: "flex",
    alignItems: "center",
    p: filterPanel.headPadding,
  } as SxProps<Theme>,

  panelTitle: {
    flex: 1,
    m: 0,
    fontSize: filterPanel.titleFontSize,
    fontWeight: filterPanel.titleFontWeight,
    color: filterPanel.titleColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  panelBody: { p: filterPanel.bodyPadding } as SxProps<Theme>,

  panelList: {
    display: "flex",
    flexDirection: "column",
    gap: filterPanel.rowGap,
  } as SxProps<Theme>,

  /**
   * The Sender / Template / Tags checklist. A popover caps it and scrolls it;
   * the drawer's pane is already a scroller with the whole height to give, so
   * there the list simply runs on.
   */
  panelScrollList: (fill: boolean) =>
    ({
      display: "flex",
      flexDirection: "column",
      gap: filterPanel.rowGap,
      ...(fill
        ? {}
        : { maxHeight: filterPanel.listMaxHeight, overflowY: "auto" }),
    }) as SxProps<Theme>,

  /** A checkbox or radio row; the whole row is the hit area. */
  panelOption: (checked: boolean) =>
    ({
      display: "flex",
      width: "100%",
      m: 0,
      p: filterPanel.optionPadding,
      boxSizing: "border-box",
      borderRadius: filterPanel.optionRadius,
      bgcolor: checked ? filterPanel.optionCheckedBg : "transparent",
      "&:hover": { bgcolor: filterPanel.optionHoverBg },
      "& .MuiFormControlLabel-label": {
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: filterPanel.optionGap,
        fontSize: filterPanel.optionFontSize,
        fontWeight: checked ? filterPanel.optionCheckedWeight : 400,
        color: filterPanel.optionColor,
      },
    }) as SxProps<Theme>,

  panelOptionText: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  /** Right-aligned aside on a row — a quick range's dates. */
  panelOptionHint: {
    ml: "auto",
    fontSize: filterPanel.hintFontSize,
    fontWeight: 400,
    color: filterPanel.hintColor,
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  panelStatusChip: (bg: string) =>
    ({
      display: "inline-flex",
      alignItems: "center",
      gap: filterPanel.statusChipGap,
      p: filterPanel.statusChipPadding,
      borderRadius: "999px",
      bgcolor: bg,
    }) as SxProps<Theme>,

  panelStatusDot: (dot: string) =>
    ({
      width: filterPanel.statusDotSize,
      height: filterPanel.statusDotSize,
      borderRadius: "50%",
      bgcolor: dot,
    }) as SxProps<Theme>,

  panelStatusLabel: (color: string) =>
    ({
      fontSize: filterPanel.statusFontSize,
      fontWeight: 700,
      color,
      lineHeight: "normal",
    }) as SxProps<Theme>,

  /** Indented under the "Custom range" radio it belongs to. */
  panelCustomDates: {
    p: filterPanel.customPadding,
  } as SxProps<Theme>,

  /**
   * The form-field box around the range picker's trigger: the shared 44px
   * field, with the focus border held while the calendar is open.
   */
  panelDateField: (open: boolean) =>
    ({
      display: "flex",
      alignItems: "center",
      height: formFieldMetrics.height,
      boxSizing: "border-box",
      px: formFieldMetrics.paddingX,
      bgcolor: filterBar.bg,
      border: `${formFieldMetrics.borderWidth} solid ${
        open ? filterBar.openBorder : filterPanel.dateBorder
      }`,
      borderRadius: formFieldMetrics.radius,
      cursor: "pointer",
      "&:hover": {
        borderColor: open ? filterBar.openBorder : filterPanel.dateHoverBorder,
      },
      "&:focus-within": { borderColor: filterBar.openBorder },
    }) as SxProps<Theme>,

  /**
   * The picker's own trigger defaults to a 380px field; here it fills the box
   * and clips a long range. An empty range reads as a prompt, not a value.
   */
  panelDateTrigger: (hasRange: boolean) =>
    ({
      minWidth: "0 !important",
      width: "100%",
      "& .MuiOutlinedInput-root": { p: 0 },
      "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
        p: 0,
        height: "auto",
        fontSize: filterPanel.optionFontSize,
        fontWeight: hasRange ? 600 : formFieldMetrics.fontWeight,
        color: hasRange ? filterPanel.optionColor : filterPanel.datePromptColor,
        textOverflow: "ellipsis",
      },
    }) as SxProps<Theme>,

  panelDateGlyph: {
    width: filterPanel.dateIconSize,
    height: filterPanel.dateIconSize,
    mr: filterPanel.dateIconGap,
    color: filterPanel.dateIconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  panelDateCaret: {
    width: filterPanel.dateCaretSize,
    height: filterPanel.dateCaretSize,
    ml: filterPanel.dateIconGap,
    color: filterPanel.dateIconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  panelField: {
    display: "flex",
    flexDirection: "column",
    gap: filterPanel.fieldGap,
    p: filterPanel.fieldPadding,
  } as SxProps<Theme>,

  panelNote: {
    fontSize: filterPanel.noteFontSize,
    color: filterPanel.noteColor,
    lineHeight: filterPanel.noteLineHeight,
  } as SxProps<Theme>,

  panelTag: (color: SignRequestsTagColor) =>
    ({
      display: "flex",
      alignItems: "center",
      height: filterPanel.tagHeight,
      p: filterPanel.tagPadding,
      borderRadius: filterPanel.tagRadius,
      bgcolor: signRequestsTagColors[color].bg,
      color: signRequestsTagColors[color].fg,
      fontSize: filterPanel.tagFontSize,
      fontWeight: 700,
      whiteSpace: "nowrap",
    }) as SxProps<Theme>,

  panelAvatar: (bg: string) =>
    ({
      width: filterPanel.avatarSize,
      height: filterPanel.avatarSize,
      bgcolor: bg,
      color: filterPanel.avatarColor,
      fontSize: filterPanel.avatarFontSize,
      fontWeight: 700,
      flexShrink: 0,
    }) as SxProps<Theme>,

  /** Name over email, for senders who share a name. */
  panelOptionStack: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  } as SxProps<Theme>,

  panelOptionSub: {
    fontSize: filterPanel.hintFontSize,
    fontWeight: 400,
    color: filterPanel.hintColor,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  panelEmpty: {
    p: filterPanel.emptyPadding,
    borderRadius: filterPanel.emptyRadius,
    bgcolor: filterPanel.emptyBg,
    fontSize: filterPanel.emptyFontSize,
    color: filterPanel.emptyColor,
    lineHeight: filterPanel.emptyLineHeight,
  } as SxProps<Theme>,

  panelEmptyLink: {
    ...underlineLink,
    textDecoration: "none",
    verticalAlign: "baseline",
    fontSize: "inherit",
    fontWeight: 700,
    lineHeight: "inherit",
    color: filterPanel.emptyLinkColor,
    "&:hover": { background: "none", textDecoration: "underline" },
  } as SxProps<Theme>,

  panelNoMatch: {
    p: filterPanel.noMatchPadding,
    fontSize: filterPanel.emptyFontSize,
    color: filterPanel.emptyColor,
  } as SxProps<Theme>,

  panelLoading: {
    display: "flex",
    justifyContent: "center",
    p: filterPanel.emptyPadding,
  } as SxProps<Theme>,

  panelManageLink: { alignSelf: "flex-start" } as SxProps<Theme>,

  panelFoot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: filterPanel.footGap,
    p: filterPanel.footPadding,
    borderTop: `1px solid ${filterPanel.footBorder}`,
    bgcolor: filterPanel.footBg,
    borderRadius: `0 0 ${filterPanel.popoverRadius} ${filterPanel.popoverRadius}`,
  } as SxProps<Theme>,

  /* ── Table header ──────────────────────────────────────────────────────── */

  /** The phone turns every row into its own card, so it has no header. */
  tableHead: (withTags: boolean) =>
    ({
      ...tableColumns(withTags),
      display: { xs: "none", sm: "grid" },
      gap: table.gap,
      alignItems: "center",
      p: table.headPadding,
      bgcolor: table.headBg,
      borderBottom: `1px solid ${table.headBorder}`,
    }) as SxProps<Theme>,

  /**
   * A header label, shown at exactly the widths its column is. `actions` is
   * the unlabelled last column and is never hidden.
   */
  tableHeadCell: (column: SignRequestsTableColumn) =>
    ({
      ...columnVisibility(column, "block"),
      fontSize: table.headFontSize,
      fontWeight: table.headFontWeight,
      letterSpacing: table.headLetterSpacing,
      textTransform: "uppercase",
      color: table.headColor,
      lineHeight: "normal",
      whiteSpace: "nowrap",
    }) as SxProps<Theme>,

  /* ── Request-row cells added by the table layout ───────────────────────── */

  senderCell: {
    ...columnVisibility("sender", "block"),
    ...textCell,
    fontSize: table.senderFontSize,
  } as SxProps<Theme>,

  createdCell: {
    ...columnVisibility("created", "block"),
    ...textCell,
  } as SxProps<Theme>,

  expiresCell: {
    ...columnVisibility("expires", "flex"),
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  } as SxProps<Theme>,

  expiresDate: textCell as SxProps<Theme>,

  expiresSoon: {
    fontSize: table.soonFontSize,
    fontWeight: table.soonFontWeight,
    color: table.soonColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  tagsCell: {
    ...columnVisibility("tags", "flex"),
    alignItems: "center",
    gap: tags.gap,
    minWidth: 0,
    overflow: "hidden",
  } as SxProps<Theme>,

  tagChip: (color: SignRequestsTagColor, single: boolean) =>
    ({
      flex: "0 1 auto",
      minWidth: 0,
      maxWidth: single ? "100%" : tags.maxWidth,
      height: tags.height,
      lineHeight: `${tags.height}px`,
      px: "8px",
      borderRadius: tags.radius,
      bgcolor: signRequestsTagColors[color].bg,
      color: signRequestsTagColors[color].fg,
      fontSize: tags.fontSize,
      fontWeight: tags.fontWeight,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }) as SxProps<Theme>,

  tagMore: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    height: tags.height,
    p: tags.morePadding,
    borderRadius: tags.radius,
    bgcolor: tags.moreBg,
    color: tags.moreColor,
    fontSize: tags.fontSize,
    fontWeight: tags.fontWeight,
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  cellEmpty: {
    fontSize: table.dateFontSize,
    color: table.emptyColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /** Colour dot beside a tag's name — larger in the Manage tags list. */
  tagDot: (color: SignRequestsTagColor, large: boolean = false) =>
    ({
      width: large ? dialogs.dotSize : tags.dotSize,
      height: large ? dialogs.dotSize : tags.dotSize,
      borderRadius: "50%",
      bgcolor: signRequestsTagColors[color].dot,
      flexShrink: 0,
    }) as SxProps<Theme>,

  tagTrigger: (open: boolean) =>
    ({
      ...iconAction(tags.triggerSize, tags.triggerRadius),
      boxSizing: "border-box",
      border: `1px solid ${
        open ? tags.triggerActiveBorder : tags.triggerBorder
      }`,
      bgcolor: open ? tags.triggerActiveBg : toolbar.bg,
      color: open ? tags.triggerActiveColor : tags.triggerColor,
      "&.Mui-disabled": { opacity: 0.55 },
    }) as SxProps<Theme>,

  tagTriggerGlyph: {
    width: tags.triggerIconSize,
    height: tags.triggerIconSize,
  } as SxProps<Theme>,

  /* ── Tag picker ────────────────────────────────────────────────────────── */

  pickerPaper: {
    width: picker.width,
    maxWidth: "calc(100vw - 32px)",
    mt: "8px",
    borderRadius: picker.radius,
    border: `1px solid ${picker.border}`,
    boxShadow: picker.shadow,
    overflow: "hidden",
  } as SxProps<Theme>,

  /** Thin bar across the top of the picker; keeps its height while idle. */
  pickerProgress: (active: boolean) =>
    ({
      height: 2,
      visibility: active ? "visible" : "hidden",
    }) as SxProps<Theme>,

  pickerSearch: {
    p: picker.searchPadding,
    borderBottom: `1px solid ${picker.divider}`,
  } as SxProps<Theme>,

  pickerHint: {
    p: picker.hintPadding,
    fontSize: picker.hintFontSize,
    color: picker.hintColor,
    lineHeight: picker.hintLineHeight,
  } as SxProps<Theme>,

  pickerList: {
    maxHeight: picker.listMaxHeight,
    overflowY: "auto",
    p: picker.listPadding,
  } as SxProps<Theme>,

  pickerItem: {
    display: "flex",
    alignItems: "center",
    gap: picker.itemGap,
    width: "100%",
    m: 0,
    p: picker.itemPadding,
    boxSizing: "border-box",
    borderRadius: picker.itemRadius,
    "&:hover": { bgcolor: picker.itemHoverBg },
    "& .MuiFormControlLabel-label": {
      display: "flex",
      alignItems: "center",
      gap: picker.itemGap,
      minWidth: 0,
      fontSize: picker.itemFontSize,
      fontWeight: picker.itemFontWeight,
      color: drawer.optionColor,
    },
  } as SxProps<Theme>,

  pickerCreate: {
    ...focusVisible,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: picker.createGap,
    width: "100%",
    p: picker.createPadding,
    borderTop: `1px solid ${picker.divider}`,
    fontFamily: "inherit",
    fontSize: picker.createFontSize,
    fontWeight: picker.createFontWeight,
    color: picker.createColor,
    textAlign: "left",
    "&:hover": { bgcolor: picker.createHoverBg },
  } as SxProps<Theme>,

  pickerCreateGlyph: {
    width: picker.createIconSize,
    height: picker.createIconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  pickerLimit: {
    display: "flex",
    alignItems: "center",
    gap: picker.limitGap,
    p: picker.limitPadding,
    borderTop: `1px solid ${picker.limitBorder}`,
    bgcolor: picker.limitBg,
    color: picker.limitColor,
    fontSize: picker.limitFontSize,
    fontWeight: picker.limitFontWeight,
  } as SxProps<Theme>,

  pickerLimitGlyph: {
    width: picker.limitIconSize,
    height: picker.limitIconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  pickerFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    p: picker.footerPadding,
    borderTop: `1px solid ${picker.divider}`,
    bgcolor: picker.footerBg,
  } as SxProps<Theme>,

  pickerCount: {
    fontSize: picker.countFontSize,
    color: picker.countColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  manageLink: {
    ...underlineLink,
    fontSize: picker.linkFontSize,
    fontWeight: picker.linkFontWeight,
    color: picker.linkColor,
  } as SxProps<Theme>,

  /* ── "All filters" drawer ──────────────────────────────────────────────── */

  drawerRoot: {
    "& .MuiBackdrop-root": { bgcolor: drawer.scrim },
  } as SxProps<Theme>,

  drawerPaper: {
    width: { xs: "100%", sm: drawer.widthTablet, md: drawer.width },
    maxWidth: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    boxShadow: drawer.shadow,
  } as SxProps<Theme>,

  drawerHead: {
    display: "flex",
    alignItems: "center",
    gap: drawer.headGap,
    p: drawer.headPadding,
    borderBottom: `1px solid ${drawer.headBorder}`,
  } as SxProps<Theme>,

  drawerHeadText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  } as SxProps<Theme>,

  drawerTitle: {
    m: 0,
    fontSize: drawer.titleFontSize,
    fontWeight: drawer.titleFontWeight,
    color: drawer.titleColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  drawerSubtitle: {
    fontSize: drawer.subtitleFontSize,
    color: drawer.subtitleColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  drawerClose: {
    ...iconAction(drawer.closeSize, drawer.closeRadius),
    color: drawer.closeColor,
    "&:hover": { bgcolor: drawer.closeHoverBg },
  } as SxProps<Theme>,

  drawerCloseGlyph: {
    width: drawer.closeIconSize,
    height: drawer.closeIconSize,
  } as SxProps<Theme>,

  drawerBody: {
    flex: 1,
    minHeight: 0,
    display: "flex",
  } as SxProps<Theme>,

  drawerRail: {
    width: { xs: drawer.railWidthPhone, sm: drawer.railWidth },
    flexShrink: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: drawer.railGap,
    p: drawer.railPadding,
    bgcolor: drawer.railBg,
    borderRight: `1px solid ${drawer.railBorder}`,
    overflowY: "auto",
  } as SxProps<Theme>,

  drawerTab: (active: boolean) =>
    ({
      ...focusVisible,
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: drawer.tabGap,
      width: "100%",
      boxSizing: "border-box",
      p: drawer.tabPadding,
      borderRadius: drawer.tabRadius,
      bgcolor: active ? drawer.tabActiveBg : "transparent",
      boxShadow: active ? drawer.tabActiveShadow : "none",
      fontFamily: "inherit",
      fontSize: drawer.tabFontSize,
      fontWeight: active ? 800 : 600,
      color: active ? drawer.tabActiveColor : drawer.tabColor,
      textAlign: "left",
      "&:hover": { bgcolor: drawer.tabActiveBg },
    }) as SxProps<Theme>,

  drawerTabLabel: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  drawerPane: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
  } as SxProps<Theme>,

  drawerPaneHead: {
    display: "flex",
    alignItems: "center",
    p: drawer.paneHeadPadding,
  } as SxProps<Theme>,

  drawerPaneTitle: {
    flex: 1,
    m: 0,
    fontSize: drawer.paneTitleFontSize,
    fontWeight: 800,
    color: drawer.titleColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  drawerPaneBody: { p: drawer.panePadding } as SxProps<Theme>,

  drawerFoot: {
    display: "flex",
    alignItems: "center",
    gap: drawer.footGap,
    p: drawer.footPadding,
    borderTop: `1px solid ${drawer.footBorder}`,
    boxShadow: drawer.footShadow,
  } as SxProps<Theme>,

  drawerApply: { flex: 1 } as SxProps<Theme>,

  /* ── Views / tags dialog bodies ────────────────────────────────────────── */

  dialogRow: {
    display: "flex",
    alignItems: "center",
    gap: dialogs.rowGap,
    py: "12px",
    borderBottom: `1px solid ${dialogs.rowBorder}`,
    "&:last-of-type": { borderBottom: "none" },
  } as SxProps<Theme>,

  dialogRowText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  } as SxProps<Theme>,

  dialogRowName: {
    flex: 1,
    minWidth: 0,
    fontSize: dialogs.nameFontSize,
    fontWeight: dialogs.nameFontWeight,
    color: dialogs.nameColor,
    lineHeight: 1.35,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  dialogRowMeta: {
    fontSize: dialogs.metaFontSize,
    color: dialogs.metaColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  dialogAction: {
    ...iconAction(dialogs.actionSize, dialogs.actionRadius),
    color: dialogs.actionColor,
    "&:hover": { bgcolor: dialogs.actionHoverBg },
  } as SxProps<Theme>,

  dialogDangerAction: {
    ...iconAction(dialogs.actionSize, dialogs.actionRadius),
    color: dialogs.dangerColor,
    "&:hover": { bgcolor: dialogs.dangerHoverBg },
  } as SxProps<Theme>,

  dialogActionGlyph: {
    width: dialogs.actionIconSize,
    height: dialogs.actionIconSize,
  } as SxProps<Theme>,

  summaryLabel: {
    mt: dialogs.summaryMarginTop,
    mb: "8px",
    fontSize: dialogs.summaryLabelFontSize,
    fontWeight: dialogs.summaryLabelFontWeight,
    color: dialogs.summaryLabelColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  summaryBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: dialogs.summaryGap,
    p: dialogs.summaryPadding,
    borderRadius: dialogs.summaryRadius,
    bgcolor: dialogs.summaryBg,
  } as SxProps<Theme>,

  summaryChip: {
    display: "flex",
    alignItems: "center",
    gap: chips.innerGap,
    minHeight: dialogs.summaryChipHeight,
    boxSizing: "border-box",
    p: dialogs.summaryChipPadding,
    borderRadius: chips.radius,
    bgcolor: chips.bg,
    border: `1px solid ${chips.border}`,
    fontSize: "12px",
    color: chips.color,
    "& b": { color: chips.keyColor },
  } as SxProps<Theme>,

  dialogNotice: {
    display: "flex",
    alignItems: "center",
    gap: dialogs.noticeGap,
    mb: "6px",
    p: dialogs.noticePadding,
    borderRadius: dialogs.noticeRadius,
    bgcolor: dialogs.noticeBg,
    color: dialogs.noticeColor,
    fontSize: dialogs.noticeFontSize,
  } as SxProps<Theme>,

  dialogNoticeGlyph: { width: 15, height: 15, flexShrink: 0 } as SxProps<Theme>,

  tagEditor: {
    display: "flex",
    flexDirection: "column",
    gap: dialogs.editorGap,
    py: "14px",
    borderBottom: `1px solid ${dialogs.rowBorder}`,
  } as SxProps<Theme>,

  swatchRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: dialogs.swatchGap,
  } as SxProps<Theme>,

  swatch: (color: SignRequestsTagColor, selected: boolean) =>
    ({
      ...focusVisible,
      width: dialogs.swatchSize,
      height: dialogs.swatchSize,
      boxSizing: "border-box",
      p: dialogs.swatchInset,
      borderRadius: "50%",
      border: `${dialogs.swatchRing} solid ${
        selected ? signRequestsTagColors[color].dot : "transparent"
      }`,
      "& > span": {
        display: "block",
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        bgcolor: signRequestsTagColors[color].dot,
      },
    }) as SxProps<Theme>,

  editorActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
  } as SxProps<Theme>,

  dialogLoading: {
    display: "flex",
    justifyContent: "center",
    p: dialogs.emptyPadding,
  } as SxProps<Theme>,

  dialogEmpty: {
    p: dialogs.emptyPadding,
    textAlign: "center",
    fontSize: dialogs.emptyFontSize,
    color: dialogs.emptyColor,
    lineHeight: dialogs.emptyLineHeight,
  } as SxProps<Theme>,
};

export default signRequestsFiltersStyles;
